/**
 * LP Brand Token Resolver — JS mirror of backend/app/brand_tokens.py
 * ────────────────────────────────────────────────────────────────
 * Produces identical token values as the Python backend so Builder
 * preview and Served LP are pixel-identical.
 *
 * Used by:
 *   - LPSectionPreview.jsx (Builder canvas + preview)
 *   - LPMiniStep.jsx (PPB LP step)
 *   - Any frontend that needs LP section tokens
 *
 * DO NOT diverge from brand_tokens.py without updating both.
 * ────────────────────────────────────────────────────────────────
 * 
 * MA-305: Now consumes shared Light Theme constants to prevent LP/Funnel drift
 */

import surfacePolicy from './sectionSurfacePolicy.json';
import { contrastSafe } from './themeEngine';
import { SHARED_LIGHT_THEME_TOKENS as SLT } from './sharedLightThemeTokens';  // MA-305: Shared token layer

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function darken(hex, pct) {
  const [r, g, b] = hexToRgb(hex);
  const f = 1 - pct / 100;
  return `#${Math.round(r * f)
    .toString(16)
    .padStart(2, "0")}${Math.round(g * f)
    .toString(16)
    .padStart(2, "0")}${Math.round(b * f)
    .toString(16)
    .padStart(2, "0")}`;
}

export const DEFAULT_BRAND = {
  accent: "#dc2626",
  surface_mode: "dark",
};

/**
 * Resolve a complete LP token set from a brand config.
 * Mirrors brand_tokens.py resolve_brand_tokens() exactly.
 *
 * @param {Object|null} brandConfig - { accent: string, surface_mode: "dark"|"light" }
 * @returns {Object} Full token set with camelCase keys
 */
export function resolveLPBrandTokens(brandConfig = null) {
  const accent = brandConfig?.accent || DEFAULT_BRAND.accent;
  const mode = brandConfig?.surface_mode || DEFAULT_BRAND.surface_mode;
  const [r, g, b] = hexToRgb(accent);
  const accentRgb = `${r},${g},${b}`;
  const isDark = mode === "dark";

  // Shared tokens (mode-independent)
  const shared = {
    accent,
    accentStrong: darken(accent, 15),
    textOnAccent: contrastSafe(accent),
    radiusLg: "16px",
    radiusMd: "12px",
    radiusBtn: "10px",
    radiusPill: "100px",
    spaceXs: "8px",
    spaceSm: "16px",
    spaceMd: "24px",
    spaceLg: "40px",
    spaceXl: "64px",
    spaceSection: "40px",
    spaceCard: "24px",
    fontHeading:
      "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif",
    fontBody:
      "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', system-ui, sans-serif",
    maxW: "780px",
    surfaceMode: mode,
  };

  if (isDark) {
    return {
      ...shared,
      accentGlow: `rgba(${accentRgb},0.25)`,
      accentLight: `rgba(${accentRgb},0.12)`,
      bgPage: "#0b1220",
      surface1: "#0f1729",
      surface2: "#111b2e",
      surface3: "#0d1424",
      cardBg: "#141e33",
      cardBorder: "1px solid rgba(255,255,255,0.06)",
      cardShadow:
        "0 8px 24px rgba(0,0,0,0.3), 0 2px 8px rgba(0,0,0,0.2)",
      cardShadowSecondary: "0 4px 12px rgba(0,0,0,0.2)",
      cardInnerHL: "inset 0 1px 0 rgba(255,255,255,0.04)",
      elevatedBg: "#1a2744",
      textPrimary: "rgba(255,255,255,0.92)",
      textSecondary: "rgba(255,255,255,0.68)",
      textMuted: "rgba(255,255,255,0.45)",
      textInverse: "#ffffff",
      textInverseSub: "rgba(255,255,255,0.82)",
      textInverseMuted: "rgba(255,255,255,0.50)",
      success: "#22c55e",
      successBg: "rgba(34,197,94,0.08)",
      successBorder: "rgba(34,197,94,0.15)",
      danger: accent,
      dangerBg: `rgba(${accentRgb},0.08)`,
      dangerBorder: `rgba(${accentRgb},0.15)`,
      borderSubtle: "1px solid rgba(255,255,255,0.06)",
      // Hero gradient (dark mode always)
      heroBg: `radial-gradient(ellipse at 20% 0%, rgba(99,102,241,0.12), transparent 50%),
        radial-gradient(ellipse at 80% 100%, rgba(59,130,246,0.08), transparent 40%),
        linear-gradient(160deg, #0f172a 0%, #131d3b 50%, #0c1529 100%)`,
      // Final CTA gradient
      finalCtaBg: `radial-gradient(ellipse at 80% 0%, rgba(99,102,241,0.10), transparent 45%),
        linear-gradient(160deg, #0f172a 0%, #131d3b 50%, #0c1529 100%)`,
    };
  }

  // LIGHT mode — mirrors brand_tokens.py light mode
  // MA-305: Consume shared Light Theme constants
  return {
    ...shared,
    accentGlow: `rgba(${accentRgb},0.15)`,
    accentLight: `rgba(${accentRgb},0.06)`,
    bgPage: SLT.page_bg,  // MA-305: Shared constant
    surface1: SLT.surface_1,  // MA-305: Shared constant
    surface2: SLT.surface_2,  // MA-305: Shared constant
    surface3: SLT.surface_3,  // MA-305: Shared constant
    cardBg: SLT.card_bg,  // MA-305: Shared constant
    cardBorder: `1px solid ${SLT.border_subtle_hex}`,  // MA-305: Use shared border
    cardShadow:
      "0 8px 24px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.03)",
    cardShadowSecondary: "0 4px 12px rgba(0,0,0,0.04)",
    cardInnerHL: "inset 0 1px 0 rgba(255,255,255,0.9)",
    elevatedBg: SLT.elevated_bg,  // MA-305: Shared constant
    textPrimary: SLT.text_primary_lp,  // MA-305: Shared constant
    textSecondary: SLT.text_secondary_lp,  // MA-305: Shared constant
    textMuted: SLT.text_muted_lp,  // MA-305: Shared constant
    textInverse: SLT.text_inverse,  // MA-305: Shared constant
    textInverseSub: "rgba(0,0,0,0.7)",
    textInverseMuted: "rgba(0,0,0,0.5)",
    success: "#16a34a",
    successBg: "#f0fdf4",
    successBorder: "#bbf7d0",
    danger: accent,
    dangerBg: "#fef2f2",
    dangerBorder: "#fecaca",
    borderSubtle: `1px solid ${SLT.border_subtle_rgba}`,  // MA-305: Shared constant (rgba format for LP)
    // Hero gradient (light mode — dark hero for contrast)
    heroBg: `radial-gradient(ellipse at 20% 0%, rgba(99,102,241,0.12), transparent 50%),
      radial-gradient(ellipse at 80% 100%, rgba(59,130,246,0.08), transparent 40%),
      linear-gradient(160deg, #0f172a 0%, #131d3b 50%, #0c1529 100%)`,
    // Final CTA gradient
    finalCtaBg: `radial-gradient(ellipse at 80% 0%, rgba(99,102,241,0.10), transparent 45%),
      linear-gradient(160deg, #0f172a 0%, #131d3b 50%, #0c1529 100%)`,
  };
}

/**
 * Get surface config for section backgrounds.
 * MA-LP-LAYOUT-027E: Reads from canonical sectionSurfacePolicy.json —
 * the single source of truth shared with backend lp_section_renderers.py.
 * @param {Object} tokens - Resolved tokens from resolveLPBrandTokens
 */

const _LEVEL_TOKEN_MAP = { 1: 'surface1', 2: 'surface2', 3: 'surface3' };

export function getSurfaceConfig(tokens) {
  tokens = tokens || resolveLPBrandTokens();
  const config = {};
  for (const [key, level] of Object.entries(surfacePolicy.assignments)) {
    const tokenKey = _LEVEL_TOKEN_MAP[level];
    if (tokenKey && tokens[tokenKey]) {
      config[key] = { bg: tokens[tokenKey] };
    }
  }
  return config;
}
